import {
  ButtonGroup,
  Checkbox,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import CustomButton from 'components/CustomButton';
import InputForm from 'components/InputForm';
import InputMaskDocument from 'components/InputMaskDocument';
import InputNumber from 'components/InputNumber';
import { InstallmentCalculatorModal } from 'components/InstallmentCalculatorModal';
import { queryClient } from 'index';
import React from 'react';
import { useForm } from 'react-hook-form';
import { createCharge } from 'services/api.service';
import { validCnpj, validCpf } from 'services/validDocument';
import { onlyNumbers } from 'utils/number';

interface CreatedLink {
  id: string;
  integrationId: string;
  amount: number;
  clientId: string;
  status: string;
  createdAt: string;
  itens: Iten[];
  chargeType: string;
  description: string;
  qrCode: string;
  txId: string;
  expirationDate: string;
  link: string;
}

interface Iten {
  id: string;
  chargeId: string;
  amount: string;
  name: string;
  quantity: number;
  unitPrice: string;
  feePercent: string;
}

type FormData = {
  document: string;
  description: string;
  amount: number;
  name: string;
  email: string;
  dueDate: string;
  releaseInDays: number;
  maxInstallments: number;
  itens: {
    description: string;
    amount: number;
  }[];
};

const CreateCharge: React.FC<{}> = () => {
  const toast = useToast();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [currentStep, setCurrentStep] = React.useState(1);
  const [showPayer, setShowPayer] = React.useState(false);
  const [showIten, setShowIten] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [infoCobranca, setInfoCobranca] = React.useState<CreatedLink | null>(null);
  const [feeFromPayer, setFeeFromPayer] = React.useState(false);
  const [showIntermediary, setShowIntermediary] = React.useState(false);

  const { control, handleSubmit, reset, setValue, trigger } = useForm<FormData>({
    defaultValues: {
      releaseInDays: 30,
      maxInstallments: 12,
    },
  });

  const createNewCharge = async (data: FormData) => {
    setLoading(true);
    try {
      const charge = await createCharge({
        ...data,
        document: onlyNumbers(data?.document),
        feeType: feeFromPayer ? 'FROM_PAYER' : 'FROM_CUSTOMER',
        intermediary: showIntermediary,
        releaseInDays: showIntermediary ? +data?.releaseInDays : 0,
        maxInstallments: data?.maxInstallments ? +data?.maxInstallments : null,
      });
      setInfoCobranca(charge);
      queryClient.refetchQueries({
        queryKey: ['findAllCharges'],
        type: 'active',
      });
      toast({
        title: 'Sucesso',
        description: 'Cobrança criada com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao criar cobrança',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const validateStep = async (step: number) => {
    switch (step) {
      case 1:
        return await trigger(['amount', 'description', 'dueDate'] as const);
      case 2:
        return true; // Step 2 only has fee selection, no validation needed
      case 3:
        if (showIntermediary) {
          return await trigger([
            'name',
            'document',
            'email',
            'releaseInDays',
            'itens.0.description',
            'itens.0.amount',
          ] as const);
        }
        return true;
      case 4:
        if (!showIntermediary) {
          const fields: Array<keyof FormData | `itens.${number}.description` | `itens.${number}.amount`> = [
            'name',
            'document',
          ];
          if (showPayer) {
            fields.push('name', 'document');
          }
          if (showIten) {
            fields.push('itens.0.description', 'itens.0.amount');
          }
          return await trigger(fields);
        }
        return true;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    const nextStep = currentStep + 1;
    if (nextStep <= (showIntermediary ? 3 : 4)) {
      const isValid = await validateStep(currentStep);
      if (isValid) {
        setCurrentStep(nextStep);
      } else {
        toast({
          title: 'Erro',
          description: 'Por favor, preencha todos os campos obrigatórios',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const calculateProgress = () => {
    const totalSteps = showIntermediary ? 3 : 4;
    const completedSteps = currentStep - 1;
    return (completedSteps / totalSteps) * 100;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <VStack
            spacing={5}
            alignItems="center"
          >
            <Text fontWeight="semibold">Dados básicos da cobrança</Text>
            <InputNumber
              control={control}
              placeholder="R$ 0,00"
              label={'Valor da cobrança'}
              name={`amount`}
              rules={{
                required: 'Campo obrigatório',
                validate: {
                  positive: (value) => value > 0 || 'O valor deve ser maior que 0',
                },
              }}
            />
            <InputForm
              control={control}
              label="Descrição adicional"
              name="description"
              placeholder="Pagamento referência a..."
              type="text"
              rules={{
                required: 'Campo obrigatório',
              }}
            />
            <InputForm
              control={control}
              label="Data de vencimento"
              name="dueDate"
              type="date"
              rules={{
                required: 'Campo obrigatório',
                validate: {
                  future: (value) => {
                    if (new Date(value) < new Date()) {
                      return 'A data de vencimento não pode ser anterior à data atual';
                    }
                    return true;
                  },
                },
              }}
            />
          </VStack>
        );
      case 2:
        return (
          <VStack
            spacing={5}
            alignItems="center"
          >
            <Text fontWeight="semibold">Configuração de taxas</Text>
            <VStack w={'100%'}>
              <HStack w={'100%'}>
                <ButtonGroup
                  w="100%"
                  isAttached
                >
                  <CustomButton
                    variant={feeFromPayer ? 'outline' : 'solid'}
                    colorScheme="blue"
                    onClick={() => setFeeFromPayer(false)}
                  >
                    Assumir taxas *
                  </CustomButton>
                  <CustomButton
                    variant={feeFromPayer ? 'solid' : 'outline'}
                    colorScheme="blue"
                    onClick={() => setFeeFromPayer(true)}
                  >
                    Repassar taxas *
                  </CustomButton>
                </ButtonGroup>
                <InstallmentCalculatorModal
                  iconButton
                  onlyOnline
                />
              </HStack>
              <Text
                fontSize={'sm'}
                alignSelf={'flex-start'}
              >
                * Somente taxas de cartão de crédito
              </Text>
              <InputForm
                control={control}
                label="Máximo de parcelas"
                name="maxInstallments"
                placeholder="Máximo de parcelas"
                type="tel"
                rules={{
                  required: 'Campo obrigatório',
                  validate: {
                    positive: (value) => +value > 0 || 'O valor deve ser maior que 0',
                    max: (value) => +value <= 12 || 'O valor máximo de parcelas é 12',
                  },
                }}
              />
            </VStack>
          </VStack>
        );
      case 3:
        return (
          <VStack
            spacing={5}
            alignItems="center"
          >
            <Text fontWeight="semibold">Tipo de cobrança</Text>
            <Checkbox
              width={'100%'}
              onChange={(e) => {
                if (!e.target.checked) {
                  setValue('name', '');
                  setValue('document', '');
                  setValue('email', '');
                  setValue('itens', []);
                }
                setShowIntermediary(e.target.checked);
                setShowIten(e.target.checked);
              }}
            >
              Intermediar cobrança?
            </Checkbox>

            <Text fontSize={'sm'}>
              O processo da cobrança será intermediado pela plataforma, e o valor será liberado no prazo acordado entre
              as partes. <br /> Caso aconteça algum problema, o pagador poderá solicitar o reembolso do valor e uma
              análise será feita para determinar o valor a ser restituído.
            </Text>

            {showIntermediary && (
              <>
                <Text fontWeight="semibold">Dados do intermediário</Text>
                <InputForm
                  control={control}
                  label="Nome"
                  name="name"
                  rules={{
                    required: 'Campo obrigatório',
                  }}
                  type="text"
                  placeholder="Nome completo"
                />
                <HStack w={'100%'}>
                  <InputMaskDocument
                    control={control}
                    label="CPF/CNPJ"
                    placeholder="Informe o CPF ou CNPJ"
                    name="document"
                    type="tel"
                    rules={{
                      required: true,
                      validate: (value) => {
                        value = onlyNumbers(value);
                        if (validCnpj(value)) return true;
                        if (validCpf(value)) return true;
                        return 'Documento não é válido';
                      },
                    }}
                  />
                </HStack>
                <InputForm
                  control={control}
                  label="E-mail"
                  name="email"
                  type="email"
                  rules={{
                    required: 'Campo obrigatório',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'E-mail não é válido',
                    },
                  }}
                  placeholder="exemplo@email.com"
                />
                <InputForm
                  control={control}
                  label="Dias de intermediação"
                  name="releaseInDays"
                  type="number"
                  placeholder="Dias de intermediação"
                />
                <Text fontWeight="semibold">Dados do item</Text>
                <InputForm
                  control={control}
                  label="Descrição"
                  name="itens[0].description"
                  type="text"
                  placeholder="Descrição do item"
                  rules={{
                    required: 'Campo obrigatório',
                  }}
                />
                <InputNumber
                  control={control}
                  placeholder="R$ 0,00"
                  label={'Valor do item'}
                  name={`itens[0].amount`}
                  rules={{
                    required: 'Campo obrigatório',
                    validate: {
                      positive: (value) => value > 0 || 'O valor deve ser maior que 0',
                    },
                  }}
                />
              </>
            )}
          </VStack>
        );
      case 4:
        return (
          <VStack
            spacing={5}
            alignItems="center"
          >
            <Text fontWeight="semibold">Informações adicionais</Text>
            {!showIntermediary && (
              <>
                <Checkbox
                  width={'100%'}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      setValue('name', '');
                      setValue('document', '');
                    }
                    setShowPayer(e.target.checked);
                  }}
                >
                  Informar dados do pagador?
                </Checkbox>
                {showPayer && (
                  <>
                    <Text fontWeight="semibold">Dados do pagador</Text>
                    <InputForm
                      control={control}
                      label="Nome"
                      name="name"
                      type="text"
                      placeholder="Nome completo"
                    />
                    <InputMaskDocument
                      control={control}
                      label="CPF/CNPJ"
                      placeholder="000.000.000-00"
                      name="document"
                      type="tel"
                      rules={{
                        required: true,
                        validate: (value) => {
                          value = onlyNumbers(value);
                          if (validCnpj(value)) return true;
                          if (validCpf(value)) return true;
                          return 'Documento não é válido';
                        },
                      }}
                    />
                  </>
                )}
                <Checkbox
                  width={'100%'}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      setValue('itens', []);
                    }
                    setShowIten(e.target.checked);
                  }}
                >
                  Adicionar item a cobrança?
                </Checkbox>
                {showIten && (
                  <>
                    <Text fontWeight="semibold">Itens</Text>
                    <InputForm
                      control={control}
                      label="Descrição"
                      name="itens[0].description"
                      type="text"
                      placeholder="Descrição do item"
                      rules={{
                        required: 'Campo obrigatório',
                      }}
                    />
                    <InputNumber
                      control={control}
                      placeholder="R$ 0,00"
                      label={'Valor do item'}
                      name={`itens[0].amount`}
                      rules={{
                        required: 'Campo obrigatório',
                        validate: {
                          positive: (value) => value > 0 || 'O valor deve ser maior que 0',
                        },
                      }}
                    />
                  </>
                )}
              </>
            )}
          </VStack>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <CustomButton
        colorScheme="brand"
        variant="solid"
        onClick={() => {
          setShowIntermediary(false);
          setShowPayer(false);
          setShowIten(false);
          onOpen();
          reset();
          setInfoCobranca(null);
          setCurrentStep(1);
        }}
      >
        Criar cobrança
      </CustomButton>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setInfoCobranca(null);
            setCurrentStep(1);
          }}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Criar cobrança</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {infoCobranca?.id ? (
                <VStack
                  spacing={5}
                  alignItems="center"
                >
                  <Text>Cobrança criada com sucesso, compartilhe o link abaixo:</Text>
                  <CustomButton
                    colorScheme="blue"
                    variant="solid"
                    onClick={() => {
                      navigator.clipboard.writeText(infoCobranca?.link);
                    }}
                  >
                    Copiar link
                  </CustomButton>
                </VStack>
              ) : (
                <>
                  <Progress
                    width={'100%'}
                    value={calculateProgress()}
                    mb={4}
                    sx={{
                      '& > div': {
                        transition: 'width 0.3s ease-in-out',
                      },
                    }}
                  />
                  {renderStepContent()}
                </>
              )}
            </ModalBody>

            <ModalFooter>
              {!infoCobranca && (
                <VStack
                  spacing={2}
                  w="100%"
                >
                  <HStack
                    w="100%"
                    justify="space-between"
                  >
                    {currentStep > 1 && (
                      <CustomButton
                        colorScheme="gray"
                        variant="outline"
                        onClick={handleBack}
                      >
                        Voltar
                      </CustomButton>
                    )}
                    {currentStep < (showIntermediary ? 3 : 4) ? (
                      <CustomButton
                        colorScheme="blue"
                        variant="solid"
                        onClick={handleSubmit(handleNext)}
                      >
                        Próximo
                      </CustomButton>
                    ) : (
                      <CustomButton
                        colorScheme="green"
                        variant="solid"
                        isLoading={loading}
                        onClick={handleSubmit(createNewCharge)}
                      >
                        Criar cobrança
                      </CustomButton>
                    )}
                  </HStack>
                </VStack>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default CreateCharge;
